'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { reviewApi, SubmitReviewPayload } from '@/services/api/review.api';

export const REVIEW_QUERY_KEYS = {
  all: ['reviews'] as const,
  eligibility: () => [...REVIEW_QUERY_KEYS.all, 'eligibility'] as const,
};

/**
 * Whether the experience-review prompt should be shown to the current user.
 * Refetches on window focus so the prompt can appear after the user places
 * their 2nd trade / completes their 2nd deposit within the same session.
 */
export const useReviewEligibility = (enabled = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: REVIEW_QUERY_KEYS.eligibility(),
    queryFn: () => reviewApi.getEligibility(),
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

export const useSubmitReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubmitReviewPayload) => reviewApi.submitReview(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.all });
    },
  });
};
